<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />

  <div v-if="!loading" class="container-fluid my-3">
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h4 class="mb-0">باركود الطلاب — {{ gradeName }} ({{ pagination.total }})</h4>
      <div class="d-flex gap-2 flex-wrap">
        <router-link :to="printLink" class="btn btn-warning">
          <i class="pi pi-print me-1"></i> طباعة الكل
        </router-link>
        <router-link :to="`/grades/${gradeSlug}/students`" class="btn btn-secondary">
          <i class="pi pi-arrow-right me-1"></i> رجوع
        </router-link>
      </div>
    </div>

    <div class="row g-2 align-items-end mb-3">
      <div class="col-lg-4 col-md-6 col-12">
        <div class="form-group d-flex flex-column">
          <label>بحث عن الطالب</label>
          <input
            type="search"
            class="form-control"
            placeholder="اسم الطالب أو رقم الحجز"
            v-model="word"
            @keyup="debounceFetch"
          >
        </div>
      </div>
      <div class="col-lg-2 col-md-6 col-6">
        <div class="form-group d-flex flex-column">
          <label>حالة الطالب</label>
          <Dropdown v-model="status" :options="statusList" optionLabel="title" @change="onFilterChange" />
        </div>
      </div>
      <div class="col-lg-2 col-md-6 col-6">
        <div class="form-group d-flex flex-column">
          <label>نوع الطالب</label>
          <Dropdown v-model="studyMode" :options="studyModeList" optionLabel="title" @change="onFilterChange" />
        </div>
      </div>
      <div class="col-lg-4 col-md-12 col-12">
        <div class="d-flex gap-2 flex-wrap">
          <button type="button" class="btn" :class="showAll ? 'btn-primary' : 'btn-outline-primary'" @click="toggleShowAll">
            <i class="pi" :class="showAll ? 'pi-list' : 'pi-eye'"></i>
            {{ showAll ? 'إظهار الصفحات' : 'عرض الكل' }}
          </button>
          <button type="button" class="btn btn-success" :disabled="exporting" @click="exportToExcel">
            <i class="pi pi-spin pi-spinner" v-if="exporting"></i>
            <i class="pi pi-file-excel" v-else></i>
            تصدير Excel
          </button>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered table-hover align-middle">
        <thead class="table-dark">
          <tr>
            <th style="width:50px">#</th>
            <th>اسم الطالب</th>
            <th style="width:100px" class="text-center">رقم الحجز</th>
            <th style="width:130px" class="text-center">QR Code</th>
            <th class="text-center">الرابط</th>
            <th style="width:110px" class="text-center">تحميل</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(student, index) in students" :key="student.user_id">
            <td>{{ pagination.from ? pagination.from + index : index + 1 }}</td>
            <td>{{ student.name }}</td>
            <td class="text-center fw-bold">{{ student.reservation_number }}</td>
            <td class="text-center py-2">
              <QrCodeLabel
                v-if="student.qr_url"
                :ref="(el) => storeQrRef(student.user_id, el)"
                :value="student.qr_url"
                :size="90"
              />
              <span v-else class="text-muted small">—</span>
            </td>
            <td class="qr-url-cell">
              <div class="d-flex align-items-center gap-1" v-if="student.qr_url">
                <input type="text" class="form-control form-control-sm" readonly :value="student.qr_url" @click="$event.target.select()">
                <button class="btn btn-sm btn-outline-secondary" title="نسخ الرابط" @click="copyUrl(student.qr_url)">
                  <i class="pi pi-copy"></i>
                </button>
                <a class="btn btn-sm btn-outline-secondary" :href="student.qr_url" target="_blank" rel="noopener" title="فتح الرابط">
                  <i class="pi pi-external-link"></i>
                </a>
              </div>
              <span v-else class="text-muted small">—</span>
            </td>
            <td class="text-center">
              <button class="btn btn-sm btn-outline-primary" :disabled="!student.qr_url" @click="downloadQr(student)">
                <i class="pi pi-download me-1"></i> تحميل
              </button>
            </td>
          </tr>
          <tr v-if="!students.length">
            <td colspan="6" class="text-center text-muted py-4">لا يوجد طلاب مطابقون</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row" v-if="!showAll && pagination.last_page > 1">
      <div class="col-12">
        <pagination :data="paginator" class="mx-auto d-flex align-items-center justify-content-center pagination" @pagination-change-page="fetchStudents">
          <template v-slot:prev-nav>&lt;</template>
          <template v-slot:next-nav>&gt;</template>
        </pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Dropdown from 'primevue/dropdown';
import pagination from 'laravel-vue-pagination';
import * as XLSX from 'xlsx/xlsx.mjs';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import QrCodeLabel from '@/components/global/QrCodeLabel.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

const STATUS_LIST = [
  { id: null, title: 'الكل' },
  { id: 1, title: 'المقبولين' },
];

const STUDY_MODE_LIST = [
  { id: null, title: 'الكل' },
  { id: 'online', title: 'أونلاين' },
  { id: 'offline', title: 'أوفلاين' },
  { id: 'hybrid', title: 'اونلاين +اوفلاين' },
];

export default {
  name: 'StudentsBarcodes',
  components: { Breadcrumb, Loading, QrCodeLabel, Dropdown, pagination },

  data() {
    return {
      students: [],
      paginator: { data: [] },
      pagination: { total: 0, from: 0, last_page: 1 },
      loading: false,
      exporting: false,

      word: '',
      status: STATUS_LIST[0],
      studyMode: STUDY_MODE_LIST[0],
      statusList: STATUS_LIST,
      studyModeList: STUDY_MODE_LIST,

      showAll: false,
      perPage: 25,
      searchTimeout: null,

      qrRefs: {}, // { [userId]: QrCodeLabel component instance }
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },

    gradeSlug() { return this.$route.params.gradeSlug; },

    grade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          ?? this.gradeStore.allGrades.find(g => g.slug === slug)
          ?? null;
    },

    gradeName() { return this.grade?.name ?? this.gradeSlug ?? ''; },

    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.gradeName, path: `/grades/${this.gradeSlug}/students` },
        { name: 'باركود الطلاب', path: this.$route.fullPath },
      ];
    },

    // "طباعة الكل" must print exactly the currently filtered population —
    // carry the active filters over as query params for the print page to re-apply.
    printLink() {
      const query = {};
      if (this.word) query.word = this.word;
      if (this.status?.id) query.status = this.status.id;
      if (this.studyMode?.id) query.study_mode = this.studyMode.id;
      return { path: `/grades/${this.gradeSlug}/students/barcodes-print`, query };
    },
  },

  methods: {
    storeQrRef(userId, el) {
      if (el) this.qrRefs[userId] = el;
      else    delete this.qrRefs[userId];
    },

    debounceFetch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchStudents(1), 1500);
    },

    onFilterChange() {
      this.fetchStudents(1);
    },

    toggleShowAll() {
      this.showAll = !this.showAll;
      this.fetchStudents(1);
    },

    buildFilterFormData() {
      const fd = new FormData();
      if (this.grade?.id) fd.append('academic_year_id', this.grade.id);
      fd.append('order_by_name', 1);
      if (this.word) fd.append('word', this.word);
      if (this.status?.id) fd.append('status', this.status.id);
      if (this.studyMode?.id) fd.append('study_mode', this.studyMode.id);
      return fd;
    },

    mapStudents(rows) {
      return rows.map(s => ({
        user_id: s.user_id ?? s.id,
        name: s.name,
        reservation_number: s.reservation_number,
        qr_url: s.qr_url,
      }));
    },

    async fetchStudents(page = 1) {
      const pageNumber = Number.isInteger(page) ? page : (parseInt(page, 10) || 1);
      this.loading = true;
      try {
        const fd = this.buildFilterFormData();
        let url = `/all_reservations?page=${pageNumber}`;
        if (this.showAll) {
          fd.append('show_all', 1);
        } else {
          url += `&per_page=${this.perPage}`;
        }

        const { data } = await axios.post(url, fd);
        this.paginator = data.data;
        this.students = this.mapStudents(data.data.data ?? []);
        this.pagination = {
          total: data.data.meta?.total ?? this.students.length,
          from: data.data.meta?.from ?? null,
          last_page: data.data.meta?.last_page ?? 1,
        };
      } catch {
        this.paginator = { data: [] };
        this.students = [];
        this.pagination = { total: 0, from: 0, last_page: 1 };
      } finally {
        this.loading = false;
      }
    },

    async copyUrl(url) {
      try {
        await navigator.clipboard.writeText(url);
        const toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
        toast.fire({ icon: 'success', title: 'تم نسخ الرابط' });
      } catch {
        Swal.fire('', 'تعذر نسخ الرابط', 'error');
      }
    },

    downloadQr(student) {
      const comp = this.qrRefs[student.user_id];
      comp?.downloadPng(`qr-${student.reservation_number}.png`);
    },

    // Excel export must cover EVERY student matching the current filters, not
    // just the current page — fetch with show_all=1 regardless of the on-screen
    // pagination/show-all state.
    async exportToExcel() {
      this.exporting = true;
      try {
        const fd = this.buildFilterFormData();
        fd.append('show_all', 1);
        const { data } = await axios.post('/all_reservations?page=1', fd);
        const rows = this.mapStudents(data.data.data ?? []);

        const sheetRows = rows.map(r => ({
          'اسم الطالب': r.name,
          'رقم الحجز': r.reservation_number,
          'الرابط': r.qr_url,
        }));
        const ws = XLSX.utils.json_to_sheet(sheetRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, `barcodes-${this.gradeName || 'students'}.xlsx`);
      } catch {
        Swal.fire('', 'تعذر تجهيز ملف Excel', 'error');
      } finally {
        this.exporting = false;
      }
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length)    await this.gradeStore.fetchAllGrades();
    await this.fetchStudents();
  },
};
</script>

<style scoped>
.qr-url-cell {
  min-width: 220px;
}
.qr-url-cell input {
  min-width: 130px;
}
</style>
